<template>
  <div class="grading-interface">
    <!-- Header with station info and progress -->
    <GradingHeader
      :station="station"
      :candidate="candidate"
      :completion-stats="completionStats"
      :total-score="totalScore"
      :max-possible-score="maxPossibleScore"
      :current-filter="currentFilter"
      :flagged-count="flaggedCount"
      @criteria-filter="setFilter"
    />
    
    <!-- Station Instructions -->
    <StationInstructions
      v-if="station.instructions"
      :instructions="station.instructions"
      :scenario="station.scenario"
    />
    
    <!-- Grading Criteria List -->
    <div class="grading-content">
      <CriteriaList
        :criteria="filteredCriteria"
        
        :scores="scores"
        :is-commenting="activeCommentId"
        @score-change="handleScoreChange"
        @comment-focus="setActiveComment"
        @comment-blur="saveComment"
      />
    </div>
    
    <!-- Global Assessment Section -->
    <GlobalAssessment
      :global-scores="globalScores"
      :comments="globalComments"
      @global-score-change="handleGlobalScoreChange"
      @comments-change="handleGlobalCommentsChange"
    />
    
    <!-- Action Buttons -->
    <GradingActions
      :completion-percentage="completionStats.percentage"
      :has-unsaved-changes="hasUnsavedChanges"
      :is-saving="isSaving"
      @save="saveGrading"
      @complete="markAsComplete"
      @next="proceedToNext"
    />
    
    <!-- Quick Scoring Panel (Floating) -->
    <QuickScoringPanel
      v-if="showQuickScoring"
      :current-criterion="currentCriterion"
      @quick-score="applyQuickScore"
      @close="closeQuickScoring"
    />
  </div>
</template>

<script>
//import { debounce } from 'lodash';
import GlobalAssessment from "src/components/OSCE/contraptions/GlobalAssessment.vue"
import QuickScoringPanel from "src/components/OSCE/contraptions/QuickScoringPanel.vue"
import GradingActions from "src/components/OSCE/contraptions/GradingActions.vue"
import GradingHeader from "src/components/OSCE/contraptions/GradingHeader.vue"
import CriteriaList from "src/components/OSCE/contraptions/CriteriaList.vue"
import StationInstructions from "src/components/OSCE/contraptions/StationInstructions.vue"

export default {
  name: 'OSCECurrent',
  components: {
    GradingHeader,
    StationInstructions,
    CriteriaList,
    GlobalAssessment,
    GradingActions,
    QuickScoringPanel
  },
  props: {
    station: {
      type: Object,
      required: true,
      default: () => ({
        id: null,
        name: '',
        criteria: [], // Ensure criteria is always an array
        instructions: '',
        scenario: '',
        duration: 600
      })
    },
    candidate: {
      type: Object,
      required: true,
      default: () => ({
        id: null,
        name: '',
        station: null,
        flagged: false
      })
    },
    initialScores: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      scores: {},
      globalScores: {
        overall: null,
        communication: null,
        professionalism: null
      },
      globalComments: {
        strengths: '',
        areas_for_improvement: '',
        general_feedback: ''
      },
      activeCommentId: null,
      currentFilter: 'all',
      hasUnsavedChanges: false,
      isSaving: false,
      showQuickScoring: false,
      currentCriterion: null,
      lastSavedState: null
    };
  },
  
  computed: {
    // Safe criteria access
    stationCriteria() {
      return this.station.criteria || [];
    },
    
    // Filter criteria based on current filter
    filteredCriteria() {
      if (this.currentFilter === 'all') return this.stationCriteria;
      if (this.currentFilter === 'ungraded') {
        return this.stationCriteria.filter(criterion => 
          !this.scores[criterion.id]?.score && this.scores[criterion.id]?.score !== 0
        );
      }
      if (this.currentFilter === 'flagged') {
        return this.stationCriteria.filter(criterion => 
          this.scores[criterion.id]?.flagged
        );
      }
      return this.stationCriteria;
    },
    
    // Calculate completion statistics
    completionStats() {
      const total = this.stationCriteria.length;
      const graded = this.stationCriteria.filter(criterion => 
        this.scores[criterion.id]?.score !== undefined && 
        this.scores[criterion.id]?.score !== null
      ).length;
      
      const percentage = total > 0 ? Math.round((graded / total) * 100) : 0;
      
      return {
        total,
        graded,
        percentage,
        remaining: total - graded
      };
    },
    
    // Calculate total score
    totalScore() {
      return Object.values(this.scores).reduce((total, score) => {
        return total + (Number(score.score) || 0);
      }, 0);
    },
    
    // Calculate maximum possible score
    maxPossibleScore() {
      return this.stationCriteria.reduce((total, criterion) => {
        return total + (Number(criterion.maxScore) || 0);
      }, 0);
    },
    
    // Check if any criteria are flagged for review
    flaggedCount() {
      return Object.values(this.scores).filter(score => score.flagged).length;
    }
  },
  
  watch: {
    // Watch for station changes to reset state
    station: {
      handler(newStation) {
        if (newStation && newStation.criteria) {
          this.initializeScores(newStation);
        }
      },
      immediate: true
    },
    
    // Watch for candidate changes
    candidate: {
      handler(newCandidate) {
        if (newCandidate) {
          this.loadCandidateScores(newCandidate);
        }
      },
      immediate: true
    },
    
    // Deep watch scores for changes
    scores: {
      handler(newScores) {
        if (!this.lastSavedState) {
          this.hasUnsavedChanges = true;
          this.debouncedAutoSave();
          return;
        }
        
        // Check if scores actually changed
        const hasChanges = JSON.stringify(newScores) !== JSON.stringify(this.lastSavedState.scores) ||
                          JSON.stringify(this.globalScores) !== JSON.stringify(this.lastSavedState.globalScores);
        
        this.hasUnsavedChanges = hasChanges;
        
        if (hasChanges) {
          this.debouncedAutoSave();
        }
      },
      deep: true
    },
    
    globalScores: {
      handler() {
        this.hasUnsavedChanges = true;
        this.debouncedAutoSave();
      },
      deep: true
    },
    
    globalComments: {
      handler() {
        this.hasUnsavedChanges = true;
        this.debouncedAutoSave();
      },
      deep: true
    }
  },
  
  mounted() {
    this.initializeComponent();
    this.setupKeyboardShortcuts();
  },
  
  beforeDestroy() {
    // Save any remaining changes before component destruction
    if (this.hasUnsavedChanges) {
      this.saveGrading();
    }
  },
  
  methods: {
    initializeComponent() {
      // Simple debounce implementation since lodash is commented out
      this.debouncedAutoSave = this.debounce(this.autoSave, 2000);
      this.initializeScores(this.station);
      this.loadCandidateScores(this.candidate);
    },
    
    // Simple debounce implementation
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },
    
    initializeScores(station) {
      if (!station || !station.criteria) {
        console.warn('Station or station.criteria is undefined');
        this.scores = {};
        return;
      }
      
      // Initialize scores structure for all criteria
      const initialScores = {};
      station.criteria.forEach(criterion => {
        initialScores[criterion.id] = {
          score: null,
          comments: '',
          flagged: false,
          timestamp: null,
          criterionId: criterion.id
        };
      });
      
      this.scores = { ...initialScores, ...this.initialScores };
      this.lastSavedState = this.getCurrentState();
    },
    
    loadCandidateScores(candidate) {
      // Use a fallback if $api is not available
      let savedScores = null;
      
      try {
        // Try to get scores from localStorage as fallback
        const storageKey = `osce_scores_${this.station.id}_${candidate.id}`;
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          savedScores = JSON.parse(stored);
        }
      } catch (error) {
        console.warn('Could not load saved scores:', error);
      }
      
      if (savedScores) {
        this.scores = { ...this.scores, ...savedScores.criteriaScores };
        this.globalScores = savedScores.globalScores || this.globalScores;
        this.globalComments = savedScores.globalComments || this.globalComments;
      }
      
      this.lastSavedState = this.getCurrentState();
      this.hasUnsavedChanges = false;
    },
    
    // Handle individual criterion scoring
    handleScoreChange({ criterionId, score, comments, flagged }) {
      const currentScore = this.scores[criterionId] || {};
      
      this.$set(this.scores, criterionId, {
        ...currentScore,
        score: score !== undefined ? score : currentScore.score,
        comments: comments !== undefined ? comments : currentScore.comments,
        flagged: flagged !== undefined ? flagged : currentScore.flagged,
        timestamp: new Date().toISOString()
      });
      
      // Emit event for parent component
      this.$emit('criteria-scored', {
        criterionId,
        score: this.scores[criterionId],
        totalScore: this.totalScore,
        completion: this.completionStats
      });
    },
    
    // Comment management
    setActiveComment(criterionId) {
      this.activeCommentId = criterionId;
    },
    
    saveComment(criterionId, comments) {
      this.handleScoreChange({ criterionId, comments });
      this.activeCommentId = null;
    },
    
    // Filter criteria
    setFilter(filterType) {
      this.currentFilter = filterType;
    },
    
    // Global assessment handling
    handleGlobalScoreChange(scores) {
      this.globalScores = { ...this.globalScores, ...scores };
      this.$emit('global-scores-updated', this.globalScores);
    },
    
    handleGlobalCommentsChange(comments) {
      this.globalComments = { ...this.globalComments, ...comments };
    },
    
    // Quick scoring functionality
    openQuickScoring(criterion) {
      this.currentCriterion = criterion;
      this.showQuickScoring = true;
    },
    
    applyQuickScore(score) {
      if (this.currentCriterion) {
        this.handleScoreChange({
          criterionId: this.currentCriterion.id,
          score: score
        });
      }
      this.closeQuickScoring();
    },
    
    closeQuickScoring() {
      this.showQuickScoring = false;
      this.currentCriterion = null;
    },
    
    // Save functionality
    async autoSave() {
      if (!this.hasUnsavedChanges) return;
      
      this.isSaving = true;
      try {
        await this.performSave();
        this.$emit('auto-save', {
          success: true,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Auto-save failed:', error);
        this.$emit('auto-save-error', error);
      } finally {
        this.isSaving = false;
      }
    },
    
    async saveGrading() {
      this.isSaving = true;
      try {
        await this.performSave();
        this.$emit('manual-save', {
          success: true,
          timestamp: new Date().toISOString(),
          data: this.getCurrentState()
        });
      } catch (error) {
        console.error('Manual save failed:', error);
        this.$emit('save-error', error);
      } finally {
        this.isSaving = false;
      }
    },
    
    async performSave() {
      const saveData = this.getCurrentState();
      
      try {
        // Try to save to localStorage as fallback
        const storageKey = `osce_scores_${this.station.id}_${this.candidate.id}`;
        localStorage.setItem(storageKey, JSON.stringify(saveData));
        
        // If $api exists, use it
        if (this.$api && typeof this.$api.saveGradingData === 'function') {
          await this.$api.saveGradingData({
            stationId: this.station.id,
            candidateId: this.candidate.id,
            criteriaScores: this.scores,
            globalScores: this.globalScores,
            globalComments: this.globalComments,
            totalScore: this.totalScore,
            maxPossibleScore: this.maxPossibleScore,
            completionPercentage: this.completionStats.percentage,
            examiner: this.$store?.state?.user?.id || 'unknown',
            timestamp: new Date().toISOString()
          });
        }
      } catch (error) {
        console.warn('Save operation completed with warnings:', error);
        // Don't throw error, just log it
      }
      
      this.lastSavedState = saveData;
      this.hasUnsavedChanges = false;
    },
    
    getCurrentState() {
      return {
        scores: JSON.parse(JSON.stringify(this.scores)),
        globalScores: JSON.parse(JSON.stringify(this.globalScores)),
        globalComments: JSON.parse(JSON.stringify(this.globalComments))
      };
    },
    
    // Completion actions
    markAsComplete() {
      this.$emit('station-complete', {
        station: this.station,
        candidate: this.candidate,
        scores: this.scores,
        globalScores: this.globalScores,
        totalScore: this.totalScore
      });
    },
    
    proceedToNext() {
      this.$emit('next-candidate', {
        currentStation: this.station,
        currentCandidate: this.candidate,
        hasUnsavedChanges: this.hasUnsavedChanges
      });
    },
    
    // Keyboard shortcuts for efficient grading
    setupKeyboardShortcuts() {
      const handleKeyPress = (event) => {
        // Ignore if typing in input fields
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
          return;
        }
        
        // Number keys for quick scoring (0-9)
        if (event.key >= '0' && event.key <= '9') {
          const score = parseInt(event.key);
          this.handleQuickKeyboardScore(score);
        }
        
        // Spacebar to toggle flag
        if (event.code === 'Space' && this.activeCommentId) {
          event.preventDefault();
          this.toggleCriterionFlag(this.activeCommentId);
        }
        
        // Ctrl+S to save
        if ((event.ctrlKey || event.metaKey) && event.key === 's') {
          event.preventDefault();
          this.saveGrading();
        }
      };
      
      document.addEventListener('keydown', handleKeyPress);
      this.$once('hook:beforeDestroy', () => {
        document.removeEventListener('keydown', handleKeyPress);
      });
    },
    
    handleQuickKeyboardScore(score) {
      // Find the first ungraded criterion or currently active one
      const criterionToScore = this.stationCriteria.find(criterion => 
        !this.scores[criterion.id]?.score && this.scores[criterion.id]?.score !== 0
      );
      
      if (criterionToScore && score <= criterionToScore.maxScore) {
        this.handleScoreChange({
          criterionId: criterionToScore.id,
          score: score
        });
      }
    },
    
    toggleCriterionFlag(criterionId) {
      const current = this.scores[criterionId];
      if (current) {
        this.handleScoreChange({
          criterionId,
          flagged: !current.flagged
        });
      }
    }
  }
}
</script>

<style scoped>
.grading-interface {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8f9fa;
}

.grading-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: white;
  margin: 0 20px 20px 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

/* Responsive design */
@media (max-width: 768px) {
  .grading-content {
    margin: 0 10px 10px 10px;
    padding: 15px;
  }
}

/* Loading state */
.grading-interface.loading {
  opacity: 0.6;
  pointer-events: none;
}

/* Saving state */
.saving-indicator {
  position: fixed;
  top: 20px;
  right: 20px;
  background: #ffc107;
  color: #212529;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.9em;
  z-index: 1000;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
}
</style>